import { useState } from "react";
import { useToast } from "../../context/ToastContext";
import { sendRequest } from "../../utils/api";
import { getUserHotel } from "../../utils/user-helper";
import { CAT_RATE, VAT_RATE } from '../../constants';

type Props = {
    show: boolean;
    onClose: () => void;
    //   terminalId: string;
};

export default function TaxModal({ show, onClose, }: Props) {
    const { showToast } = useToast();

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<null | {
        totalSales: number;
        VAT: number;
        CAT: number;
    }>(null);

    const [tax, setTax] = useState({ vat: 0, cat: 0 });

    const calculateTax = async () => {
        if (!startDate || !endDate) return;

        setLoading(true);
        try {
            // Fetch filtered sales from your backend
            const response = await sendRequest("GET", `/sales?startDate=${startDate}&endDate=${endDate}`);

            const { sales } = response || [];
            
            const totalSales = (sales ?? []).reduce((sum: number, s: any) => sum + s.amount, 0);

            const data = await getUserHotel();
            if (!data) return;

            const { hotel } = data;

            const vatRate = hotel?.preference?.vatRate ?? VAT_RATE;
            const catRate = hotel?.preference?.catRate ?? CAT_RATE;
            const VAT = totalSales * (vatRate / 100);
            const CAT = totalSales * (catRate / 100);

            setTax({ vat: vatRate, cat: catRate })
            setResults({ totalSales, VAT, CAT });
        } catch (err) {
            console.error(err);
            showToast("Failed to calculate tax.", { type: "error" });
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-md p-6 shadow-lg relative">
                <h2 className="text-xl font-bold mb-4">Calculate Tax</h2>

                {/* Dates */}
                <div className="flex flex-col sm:flex-row gap-4 mb-4">
                    <div className="flex flex-col w-full">
                        <label className="text-sm text-gray-600 mb-1">Start Date</label>
                        <input
                            type="date"
                            className="border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-400 focus:outline-none w-full"
                            value={startDate}
                            onChange={(e) => {
                                setStartDate(e.target.value)
                            }}
                        />
                    </div>
                    <div className="flex flex-col w-full">
                        <label className="text-sm text-gray-600 mb-1">End Date</label>
                        <input
                            type="date"
                            className="border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-400 focus:outline-none w-full"
                            value={endDate}
                            onChange={(e) => {
                                const date = e.target.value;
                                if (!startDate) {
                                    showToast("Please select a start date first.", { type: "info" });
                                    return;
                                }

                                if (date < startDate) {
                                    showToast("End date cannot be earlier than start date.", { type: "warning" });
                                    return;
                                }
                                setEndDate(e.target.value)
                            }}
                        />
                    </div>
                </div>

                {/* Calculate Button */}
                <button
                    onClick={calculateTax}
                    disabled={!startDate || !endDate || loading}
                    className={`w-full px-4 py-2 rounded-md font-medium text-white ${!startDate || !endDate
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700"
                        }`}
                >
                    {loading ? "Calculating..." : "Calculate"}
                </button>

                {/* Results */}
                {results && (
                    <div className="mt-6 bg-gray-50 p-5 rounded-md space-y-4 font-sans">
                        <div className="flex justify-between">
                            <span className="text-gray-500 text-sm uppercase tracking-wide">Total Sales</span>
                            <span className="text-gray-900 text-lg sm:text-xl font-bold">
                                ₦{results.totalSales.toLocaleString()}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500 text-sm uppercase tracking-wide">VAT ({tax.vat}%)</span>
                            <span className="text-gray-900 text-lg sm:text-xl font-bold">
                                ₦{results.VAT.toLocaleString()}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500 text-sm uppercase tracking-wide">CAT ({tax.cat}%)</span>
                            <span className="text-gray-900 text-lg sm:text-xl font-bold">
                                ₦{results.CAT.toLocaleString()}
                            </span>
                        </div>
                    </div>
                )}


                {/* Close */}
                <button
                    onClick={
                        () => {
                            onClose();
                            setResults(null);
                            setStartDate("");
                            setEndDate("");
                        }
                    }
                    className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 font-bold text-lg"
                >
                    &times;
                </button>
            </div>
        </div>
    );
}
